import {
  User,
  Mail,
  Phone,
  CheckCircle,
  Loader2,
  Calendar,
  Clock,
  ArrowLeft,
} from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
}

export const BlueprintAISignup: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    // Last name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    // Phone number validation (basic)
    const phoneRegex = /^[\d\s\-\(\)\+]+$/;
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (
      !phoneRegex.test(formData.phoneNumber) ||
      formData.phoneNumber.replace(/\D/g, "").length < 10
    ) {
      newErrors.phoneNumber = "Please enter a valid phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Format the data to send to Zapier
      const webhookData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        eventTitle: "Blueprinting AI Class",
        source: "Dedicated Signup Page",
        submittedAt: new Date().toISOString(),
      };

      // Send to Zapier webhook (no Content-Type header to avoid CORS preflight)
      const response = await fetch(
        "https://hooks.zapier.com/hooks/catch/16772391/ur5vujq/",
        {
          method: "POST",
          body: JSON.stringify(webhookData),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to submit form");
      }

      console.log("Form submitted successfully:", webhookData);

      setIsSuccess(true);

      // Reset form after 3 seconds and allow them to sign up again
      setTimeout(() => {
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phoneNumber: "",
        });
        setIsSuccess(false);
      }, 3000);
    } catch (error) {
      console.error("Signup error:", error);
      setErrors({ email: "Something went wrong. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange =
    (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate("/calendar")}
              className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600 hover:text-gray-900"
              aria-label="Back to calendar"
            >
              <ArrowLeft className="h-5 w-5" />
              <span className="text-sm font-medium">Back to Calendar</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-6 sm:p-8 md:p-12 text-white">
            <div className="text-center">
              {/* Logo */}
              <div className="mb-4 sm:mb-6">
                <img
                  src="/aig_consulting_logos_v4_lightLogoFull.png"
                  alt="AIG Consulting"
                  className="h-24 sm:h-32 md:h-40 lg:h-48 xl:h-56 w-auto mx-auto object-contain"
                  onError={(e) => {
                    // Fallback to dark logo if light logo doesn't exist
                    e.currentTarget.src =
                      "/aig_consulting_logos_v4_darkLogoFull.png";
                  }}
                />
              </div>

              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3">
                Join Our Blueprinting AI Class
              </h2>
              <p className="text-indigo-100 text-base sm:text-lg mb-4 sm:mb-6 max-w-xl mx-auto">
                Learn how to leverage AI for business strategy and innovation
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-sm text-indigo-200">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="text-sm sm:text-base">Weekly Sessions</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="text-sm sm:text-base font-semibold">
                    Thursdays at 3:00 PM Mountain Time
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Success State */}
          {isSuccess ? (
            <div className="p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-3">
                You're All Set!
              </h3>
              <p className="text-lg text-gray-600 mb-2">
                We've received your registration for Blueprinting AI
              </p>
              <p className="text-sm text-gray-500">
                Check your email for confirmation details and next steps.
              </p>
              <div className="mt-8">
                <button
                  onClick={() => navigate("/calendar")}
                  className="text-indigo-600 hover:text-indigo-700 font-medium inline-flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  View Calendar
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Form Section */}
              <div className="p-8">
                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Sign Up Now
                  </h3>
                  <p className="text-gray-600">
                    Fill out the form below to reserve your spot in our next
                    class
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* First Name */}
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="firstName"
                        value={formData.firstName}
                        onChange={handleChange("firstName")}
                        className={`block w-full pl-10 pr-3 py-3 border ${
                          errors.firstName
                            ? "border-red-300"
                            : "border-gray-300"
                        } rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-base`}
                        placeholder="John"
                        disabled={isSubmitting}
                      />
                    </div>
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.firstName}
                      </p>
                    )}
                  </div>

                  {/* Last Name */}
                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="lastName"
                        value={formData.lastName}
                        onChange={handleChange("lastName")}
                        className={`block w-full pl-10 pr-3 py-3 border ${
                          errors.lastName ? "border-red-300" : "border-gray-300"
                        } rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-base`}
                        placeholder="Doe"
                        disabled={isSubmitting}
                      />
                    </div>
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.lastName}
                      </p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Email <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="email"
                        id="email"
                        value={formData.email}
                        onChange={handleChange("email")}
                        className={`block w-full pl-10 pr-3 py-3 border ${
                          errors.email ? "border-red-300" : "border-gray-300"
                        } rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-base`}
                        placeholder="john@example.com"
                        disabled={isSubmitting}
                      />
                    </div>
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* Phone Number */}
                  <div>
                    <label
                      htmlFor="phoneNumber"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Phone className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="tel"
                        id="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange("phoneNumber")}
                        className={`block w-full pl-10 pr-3 py-3 border ${
                          errors.phoneNumber
                            ? "border-red-300"
                            : "border-gray-300"
                        } rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-base`}
                        placeholder="(555) 123-4567"
                        disabled={isSubmitting}
                      />
                    </div>
                    {errors.phoneNumber && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.phoneNumber}
                      </p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold py-4 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2 text-base"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <span>Complete Registration</span>
                    )}
                  </button>

                  <p className="text-xs text-gray-500 text-center mt-4">
                    By signing up, you'll receive confirmation and reminder
                    emails for this class.
                  </p>
                </form>
              </div>

              {/* Benefits Section */}
              <div className="bg-gray-50 px-8 py-6 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">
                  What You'll Learn:
                </h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>
                      AI-powered business strategy and planning techniques
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>
                      Practical frameworks for implementing AI in your
                      organization
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Hands-on exercises and real-world case studies</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>
                      Networking with fellow innovators and AI enthusiasts
                    </span>
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default BlueprintAISignup;
